import { toRegionalSpelling } from "./locale";
import type { RegionId } from "./regions";
import { getFrictionPoint } from "./sector-catalog";
import type {
  CalculatorStep,
  MetricKey,
  QuestionDefinition,
  QuestionFieldKind,
  StepQuestions,
  WorkflowId,
} from "./types";

const QUESTION_IDS = {
  labor: ["labor-headcount", "labor-time-percent", "labor-salary"],
  yieldPenalty: ["yield-volume", "yield-penalty"],
  untapped: [
    "untapped-volume",
    "untapped-actionable-percent",
    "untapped-value",
  ],
} as const;

function mapQuestionType(type: string): QuestionFieldKind {
  if (type === "percentage") return "percent";
  if (type === "currency") return "currency";
  return "count";
}

function mapCategory(category: string): MetricKey | null {
  if (category === "Labor Waste") return "labor";
  if (category === "Yield Penalty") return "yieldPenalty";
  if (category === "Untapped Value") return "untapped";
  return null;
}

/** Strip currency hint parentheticals; UI already shows £/$ via input chrome. */
function cleanQuestionLabel(label: string): string {
  return label.replace(/\s*\([£$]\)/gu, "").trim();
}

function localizeQuestion(
  question: QuestionDefinition,
  regionId?: RegionId | null,
): QuestionDefinition {
  return {
    ...question,
    label: toRegionalSpelling(question.label, regionId),
  };
}

function emptyStepQuestions(): StepQuestions {
  const placeholder = (id: string): QuestionDefinition => ({
    id,
    label: "",
    kind: "count",
  });
  return {
    labor: [
      placeholder(QUESTION_IDS.labor[0]),
      placeholder(QUESTION_IDS.labor[1]),
      placeholder(QUESTION_IDS.labor[2]),
    ],
    yieldPenalty: [
      placeholder(QUESTION_IDS.yieldPenalty[0]),
      placeholder(QUESTION_IDS.yieldPenalty[1]),
    ],
    untapped: [
      placeholder(QUESTION_IDS.untapped[0]),
      placeholder(QUESTION_IDS.untapped[1]),
      placeholder(QUESTION_IDS.untapped[2]),
    ],
  };
}

/**
 * Resolve calculator questions for a sector × friction (workflow) pair
 * from `src/data/*.json`.
 */
export function getStepQuestions(
  businessSector: string | null | undefined,
  workflowId: WorkflowId,
  regionId?: RegionId | null,
): StepQuestions {
  const friction = getFrictionPoint(businessSector, workflowId);
  if (!friction) {
    return emptyStepQuestions();
  }

  const byMetric: Record<MetricKey, QuestionDefinition[]> = {
    labor: [],
    yieldPenalty: [],
    untapped: [],
  };

  for (const raw of friction.questions) {
    const metric = mapCategory(raw.category);
    if (!metric) continue;
    const ids = QUESTION_IDS[metric];
    const index = byMetric[metric].length;
    const id = ids[index] ?? `${metric}-${raw.v}`;
    byMetric[metric].push({
      id,
      label: cleanQuestionLabel(raw.question),
      kind: mapQuestionType(raw.type),
    });
  }

  const [labor0, labor1, labor2] = byMetric.labor;
  const [yield0, yield1] = byMetric.yieldPenalty;
  const [untapped0, untapped1, untapped2] = byMetric.untapped;

  if (
    !labor0 ||
    !labor1 ||
    !labor2 ||
    !yield0 ||
    !yield1 ||
    !untapped0 ||
    !untapped1 ||
    !untapped2
  ) {
    return emptyStepQuestions();
  }

  return {
    labor: [
      localizeQuestion(labor0, regionId),
      localizeQuestion(labor1, regionId),
      localizeQuestion(labor2, regionId),
    ],
    yieldPenalty: [
      localizeQuestion(yield0, regionId),
      localizeQuestion(yield1, regionId),
    ],
    untapped: [
      localizeQuestion(untapped0, regionId),
      localizeQuestion(untapped1, regionId),
      localizeQuestion(untapped2, regionId),
    ],
  };
}

export function getQuestionsForStep(
  businessSector: string | null | undefined,
  workflowId: WorkflowId,
  step: CalculatorStep,
  regionId?: RegionId | null,
): readonly QuestionDefinition[] {
  const questions = getStepQuestions(businessSector, workflowId, regionId);
  if (step === 1) return questions.labor;
  if (step === 2) return questions.yieldPenalty;
  return questions.untapped;
}

/** True when sector + friction resolve to a full 3+2+3 question set. */
export function hasSectorFrictionQuestions(
  businessSector: string | null | undefined,
  workflowId: WorkflowId,
): boolean {
  const questions = getStepQuestions(businessSector, workflowId);
  return (
    questions.labor.every((q) => q.label.length > 0) &&
    questions.yieldPenalty.every((q) => q.label.length > 0) &&
    questions.untapped.every((q) => q.label.length > 0)
  );
}
